/*
  The table filter's input (§6.5), migrated off MUI's FormControl / InputLabel /
  OutlinedInput / InputAdornment and off the Emotion pragma.

  It is only the shell. What the typed text MATCHES is filtering.ts in the DataTable
  folder — the descriptor's filterText, i.e. the visible rendered columns — not this
  component, and not the old JSON.stringify(row) helper it replaces.

  What changed beyond the styling:
    - the clear affordance is a real <button>. It was a click handler on an
      InputAdornment: no keyboard, no accessible name, invisible to a screen reader.
    - clearing emits immediately instead of waiting out the debounce. Pressing X and
      watching the old results sit there for another 300ms reads as a broken button.
    - the debounce is cancelled on unmount, and calls the CURRENT onChange rather than
      the one captured on first render.

  It does not reset the page itself — the consumer's setFilter does, because it is the
  same action (tableState.ts). This component holds only the text a visitor is mid-way
  through typing.
*/

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";
import { Search, X } from "lucide-react";

import { CONTROL_BASE, CONTROL_LABEL, FOCUS_RING } from "components/styles";

/* Long enough to skip the keystrokes, short enough not to feel stuck. Unchanged. */
export const FILTER_DEBOUNCE_MS = 300;

export interface FilterSearchProps {
  onChange: (value: string) => void;
  label?: string;
  /* Supply one to keep it stable across renders; otherwise one is generated. */
  id?: string;
  placeholder?: string;
  /* Renders the label sr-only. It is still the input's accessible name. */
  hideLabel?: boolean;
  /* Width and layout for the wrapper — it fills its parent by default. */
  className?: string;
  /* id of an element describing the field, e.g. "searches loaded rows only" (§6.5). */
  describedBy?: string;
}

/* React 17 has no useId; the same module counter idiom as components/Select.tsx. */
let filterSearchInstanceCount = 0;

const useFilterSearchId = (provided?: string): string => {
  const generated = useRef<string>();
  if (!generated.current) {
    filterSearchInstanceCount += 1;
    generated.current = `pcg-filter-search-${filterSearchInstanceCount}`;
  }
  return provided ?? generated.current;
};

const FilterSearch = (props: FilterSearchProps) => {
  const {
    onChange,
    label = "Search Cards",
    id,
    placeholder,
    hideLabel = false,
    className = "",
    describedBy,
  } = props;

  const [value, setValue] = useState("");
  const inputId = useFilterSearchId(id);

  /*
    The debounced function is built once — rebuilding it per render would reset the timer
    on every keystroke and it would never fire — so it reads onChange through a ref rather
    than closing over the first one it saw. The old version closed over it and silenced
    the eslint warning.
  */
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const emit = useMemo(
    () =>
      debounce((next: string) => {
        onChangeRef.current(next);
      }, FILTER_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => emit.cancel(), [emit]);

  const handleChange = (next: string) => {
    setValue(next);
    emit(next);
  };

  /* Immediate, not debounced: a pending keystroke must not land after the clear. */
  const handleClear = useCallback(() => {
    setValue("");
    emit.cancel();
    onChangeRef.current("");
  }, [emit]);

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <label htmlFor={inputId} className={hideLabel ? "sr-only" : CONTROL_LABEL}>
        {label}
      </label>
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-pcg-textMuted"
        />
        {/*
          type="text", not type="search": WebKit draws its own clear button on a search
          input and the visitor would be looking at two of them.
        */}
        <input
          id={inputId}
          name={inputId}
          type="text"
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          aria-describedby={describedBy}
          onChange={(event) => handleChange(event.target.value)}
          className={`w-full py-2 pl-9 pr-10 ${CONTROL_BASE} placeholder:text-pcg-textDim ${FOCUS_RING}`}
        />
        {value ? (
          <button
            type="button"
            aria-label={`Clear ${label}`}
            onClick={handleClear}
            className={`absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-pcg-textMuted hover:text-pcg-text ${FOCUS_RING}`}
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default FilterSearch;
